import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { HubContactTokenInput } from "@/components/ui/HubContactTokenInput";
import { HubSaveButton } from "@/components/ui/HubSaveButton";
import { HubSettingsFooter, HubSettingsSection } from "@/components/settings/HubSettingsSection";
import { ApiError } from "@/lib/api";
import { ManufacturingLocationChip } from "@/lib/manufacturingLocationChip";
import { useHubModuleAdmin } from "@/lib/useHubModuleAdmin";
import { formatTimestamp } from "@/lib/utils";

import {
  fetchTransferShippedNotificationSettings,
  patchTransferShippedNotificationSettings,
  TRANSFER_SHIPPED_NOTIFICATION_SETTINGS_QUERY_KEY,
  type TransferShippedNotificationLocationRecipients,
  type TransferShippedNotificationRecipient,
} from "./transferShippedNotificationApi";

const SECTION_HELP =
  "When an SGS transfer is marked picked up / shipped, Freshdesk sends vendors@smartgridsolutions.com outbound email to the contacts configured for the receiving location. Search the hub contact directory (hub, Freshsales, or Freshdesk). Names and emails stay linked to the contact record. The transfer document PDF is attached.";

function recipientsMapFromRows(
  rows: TransferShippedNotificationLocationRecipients[],
): Map<number, TransferShippedNotificationRecipient[]> {
  const map = new Map<number, TransferShippedNotificationRecipient[]>();
  for (const row of rows) {
    map.set(row.destinationLocationId, [...row.notifyRecipients]);
  }
  return map;
}

function rowsFromRecipientsMap(
  map: Map<number, TransferShippedNotificationRecipient[]>,
): Array<{
  destinationLocationId: number;
  notifyRecipients: Array<{ hubContactId: number }>;
}> {
  return [...map.entries()]
    .filter(([, recipients]) => recipients.length > 0)
    .map(([destinationLocationId, notifyRecipients]) => ({
      destinationLocationId,
      notifyRecipients: notifyRecipients.map((recipient) => ({ hubContactId: recipient.hubContactId })),
    }))
    .sort((left, right) => left.destinationLocationId - right.destinationLocationId);
}

function contactRecipientsFingerprint(recipients: TransferShippedNotificationRecipient[]): string {
  return [...recipients]
    .map((recipient) => String(recipient.hubContactId))
    .sort()
    .join("\n");
}

function transferShippedRecipientsMapEqual(
  left: Map<number, TransferShippedNotificationRecipient[]>,
  right: Map<number, TransferShippedNotificationRecipient[]>,
): boolean {
  if (left.size !== right.size) {
    return false;
  }
  for (const [locationId, leftRecipients] of left) {
    const rightRecipients = right.get(locationId);
    if (rightRecipients == null) {
      return false;
    }
    if (contactRecipientsFingerprint(leftRecipients) !== contactRecipientsFingerprint(rightRecipients)) {
      return false;
    }
  }
  return true;
}

export function TransferShippedNotificationSettingsSection(): JSX.Element {
  const queryClient = useQueryClient();
  const canEdit = useHubModuleAdmin("manufacturing_management");
  const [recipientsByLocation, setRecipientsByLocation] = useState<
    Map<number, TransferShippedNotificationRecipient[]>
  >(new Map());
  const [initialized, setInitialized] = useState(false);

  const settingsQuery = useQuery({
    queryKey: TRANSFER_SHIPPED_NOTIFICATION_SETTINGS_QUERY_KEY,
    queryFn: fetchTransferShippedNotificationSettings,
  });

  useEffect(() => {
    if (settingsQuery.data != null && !initialized) {
      setRecipientsByLocation(recipientsMapFromRows(settingsQuery.data.recipientsByDestination));
      setInitialized(true);
    }
  }, [settingsQuery.data, initialized]);

  const saveMutation = useMutation({
    mutationFn: () => patchTransferShippedNotificationSettings(rowsFromRecipientsMap(recipientsByLocation)),
    onSuccess: (data) => {
      queryClient.setQueryData(TRANSFER_SHIPPED_NOTIFICATION_SETTINGS_QUERY_KEY, data);
      setRecipientsByLocation(recipientsMapFromRows(data.recipientsByDestination));
      setInitialized(true);
    },
  });

  const locations = settingsQuery.data?.locations ?? [];
  const updatedAt = settingsQuery.data?.updatedAt ?? null;

  const persistedRecipients = useMemo(() => {
    if (settingsQuery.data == null) {
      return null;
    }
    return recipientsMapFromRows(settingsQuery.data.recipientsByDestination);
  }, [settingsQuery.data]);

  const isDirty = useMemo(() => {
    if (!initialized || persistedRecipients == null) {
      return false;
    }
    return !transferShippedRecipientsMapEqual(recipientsByLocation, persistedRecipients);
  }, [initialized, persistedRecipients, recipientsByLocation]);

  function setRecipientsForLocation(
    locationId: number,
    notifyRecipients: TransferShippedNotificationRecipient[],
  ): void {
    setRecipientsByLocation((current) => {
      const next = new Map(current);
      next.set(locationId, notifyRecipients);
      return next;
    });
  }

  return (
    <HubSettingsSection title="Transfer shipped notifications" hoverHint={SECTION_HELP}>
      {settingsQuery.isLoading ? (
        <p className="text-sm text-slate-500">Loading settings…</p>
      ) : settingsQuery.isError ? (
        <p className="text-sm text-rose-700" role="alert">
          {settingsQuery.error instanceof ApiError
            ? settingsQuery.error.message
            : "Could not load transfer shipped notification settings."}
        </p>
      ) : locations.length === 0 ? (
        <p className="text-sm text-slate-600">
          No warehouse locations are configured. Add Megaventory locations under Admin → Locations.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[36rem] table-fixed border-collapse text-sm">
            <colgroup>
              <col className="w-[28%]" />
              <col className="w-[8.5rem]" />
              <col />
            </colgroup>
            <thead>
              <tr className="text-left text-xs text-slate-600">
                <th className="border-b border-slate-200 py-1.5 pr-4 font-medium">Location</th>
                <th className="border-b border-slate-200 py-1.5 pr-4 font-medium">Abbreviation</th>
                <th className="border-b border-slate-200 py-1.5 font-medium">Recipients</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {locations.map((location) => (
                <tr key={location.destinationLocationId} className="align-top">
                  <td
                    className="max-w-0 truncate py-2 pr-4 font-medium text-slate-900"
                    title={location.displayName}
                  >
                    {location.displayName}
                  </td>
                  <td className="whitespace-nowrap py-2 pr-4">
                    <ManufacturingLocationChip
                      locationId={location.destinationLocationId}
                      fallbackLabel={location.abbreviation}
                    />
                  </td>
                  <td className="py-2">
                    <HubContactTokenInput
                      id={`transfer-shipped-notify-${String(location.destinationLocationId)}`}
                      recipients={recipientsByLocation.get(location.destinationLocationId) ?? []}
                      onRecipientsChange={(next) =>
                        setRecipientsForLocation(location.destinationLocationId, next)
                      }
                      disabled={!canEdit}
                      placeholder="Search contacts…"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <HubSettingsFooter>
        <div className="space-y-2">
          {saveMutation.error instanceof ApiError ? (
            <p className="text-sm text-rose-700" role="alert">
              {saveMutation.error.message}
            </p>
          ) : null}
          <HubSaveButton
            type="button"
            dirty={isDirty}
            disabled={!canEdit || settingsQuery.isLoading || locations.length === 0}
            blockedHint="Manufacturing management administrators can edit transfer shipped notification recipients."
            isLoading={saveMutation.isPending}
            onClick={() => saveMutation.mutate()}
          >
            Save notification recipients
          </HubSaveButton>
        </div>
      </HubSettingsFooter>

      {updatedAt != null ? (
        <p className="mt-2 text-xs text-slate-500">Last saved {formatTimestamp(updatedAt)}</p>
      ) : null}
    </HubSettingsSection>
  );
}
